/**
 * Mega Menu Components
 *
 * A set of reusable, composable components for building mega-menu navigation.
 * These components are headless/unstyled primitives that handle the interaction
 * logic (dropdowns, mobile menus, keyboard navigation) while allowing apps
 * to configure their own navigation items and routes.
 *
 * @example
 * ```gts
 * import { MegaMenu } from 'tio-ui/components/mega-menu';
 *
 * const NAV_ITEMS = [
 *   { id: 'home', icon: PhHouse, label: 'Home', route: 'authenticated.home' },
 *   { id: 'settings', icon: PhGear, label: 'Settings', dropdown: [...] },
 * ];
 *
 * <MegaMenu @items={{NAV_ITEMS}} @isFullscreen={{false}} />
 * ```
 */

import Component from '@glimmer/component';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { cached, tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { LinkTo } from '@ember/routing';
import { PhCaretDown, PhDotsThree, PhList, PhX } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * Modifier that keeps a wide dropdown panel inside the viewport: if the
 * panel overflows the right edge, shift it left (down to the left margin).
 */
const clampPanel = modifier((element: HTMLElement) => {
  const margin = 16;
  const clamp = () => {
    element.style.marginLeft = '';
    const rect = element.getBoundingClientRect();
    const overflowRight = rect.right - (window.innerWidth - margin);
    if (overflowRight > 0) {
      const shift = Math.min(overflowRight, Math.max(rect.left - margin, 0));
      element.style.marginLeft = `-${shift}px`;
    }
  };
  clamp();
  window.addEventListener('resize', clamp);
  return () => window.removeEventListener('resize', clamp);
});

/**
 * Configuration for a flyout submenu item (leaf node)
 */
export interface MegaMenuFlyoutItem {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** Ember route to navigate to */
  route: string;
}

/**
 * Configuration for a dropdown menu item with a direct route
 */
export interface MegaMenuDropdownItemWithRoute {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** Ember route to navigate to */
  route: string;
  /** Optional stat/badge to display (e.g., "$125/mo", "84/120") */
  stat?: string;
  /** No flyout for route items */
  flyout?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Configuration for a dropdown menu item with a flyout submenu
 */
export interface MegaMenuDropdownItemWithFlyout {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** No direct route for flyout items */
  route?: never;
  /** Optional stat/badge to display */
  stat?: string;
  /** Nested flyout submenu */
  flyout: MegaMenuFlyoutItem[];
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Configuration for a dropdown menu item (either route or flyout)
 */
export type MegaMenuDropdownItem = MegaMenuDropdownItemWithRoute | MegaMenuDropdownItemWithFlyout;

/**
 * Navigation item with a direct route (no dropdown)
 */
export interface MegaMenuItemWithRoute {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Ember route to navigate to */
  route: string;
  /** Never has a dropdown */
  dropdown?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
  /** Push this item (and everything after it) to the end of the bar */
  alignEnd?: boolean;
  /** Render only the icon in the bar (label becomes the accessible name) */
  iconOnly?: boolean;
  /** Fold this item into the desktop bar's "More" dropdown (mobile keeps it inline) */
  collapseInBar?: boolean;
}

/**
 * Navigation item that performs an action instead of transitioning routes.
 */
export interface MegaMenuItemWithAction {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Callback to run when selected */
  onClick: () => void;
  /** Never has a direct route */
  route?: never;
  /** Never has a dropdown */
  dropdown?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
  /** Push this item (and everything after it) to the end of the bar */
  alignEnd?: boolean;
  /** Render only the icon in the bar (label becomes the accessible name) */
  iconOnly?: boolean;
  /** Action items never collapse — the "More" panel only renders links */
  collapseInBar?: never;
}

/**
 * Navigation item with a dropdown menu
 */
export interface MegaMenuItemWithDropdown {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Never has a direct route */
  route?: never;
  /** Dropdown menu items */
  dropdown: MegaMenuDropdownItem[];
  /** Optional route for the "View Dashboard" CTA (overrides auto-detection) */
  indexRoute?: string;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
  /** Push this item (and everything after it) to the end of the bar */
  alignEnd?: boolean;
  /** Fold this item into the desktop bar's "More" dropdown (mobile keeps it inline) */
  collapseInBar?: boolean;
}

export type MegaMenuItem =
  MegaMenuItemWithRoute | MegaMenuItemWithDropdown | MegaMenuItemWithAction;

export interface MegaMenuSignature {
  Args: {
    /** Navigation items configuration */
    items: MegaMenuItem[];
    /** Whether layout is fullscreen (affects container width) */
    isFullscreen?: boolean;
    /** Optional logo/brand component to render */
    logo?: ComponentLike<{ Element: HTMLElement }>;
    /** Label for the mobile brand display */
    brandLabel?: string;
    /** Label for the synthesized overflow item that holds `collapseInBar` items */
    moreLabel?: string;
  };
  Blocks: {
    /** Optional trailing desktop bar content, such as a global search trigger */
    default?: [];
    /**
     * Optional actions rendered in the mobile top bar, immediately left of the
     * hamburger toggle (e.g. a global search trigger) so they stay reachable on
     * touch without opening the menu panel.
     */
    mobileActions?: [];
  };
  Element: HTMLElement;
}

/**
 * Helper to filter visible dropdown items
 */
const getVisibleDropdownItems = (items: MegaMenuDropdownItem[]): MegaMenuDropdownItem[] => {
  return items.filter((item) => item.isVisible !== false);
};

/** A leaf link inside a dropdown panel, with its flattened roving-tab index. */
interface PanelLeaf {
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  label: string;
  description: string;
  route: string;
  stat?: string;
  flatIndex: number;
}

/** A column in the dropdown panel: ungrouped links (label null) or a titled section. */
interface PanelGroup {
  label: string | null;
  items: PanelLeaf[];
}

/**
 * Flatten a dropdown config into panel columns: route items form an untitled
 * leading column; each flyout item becomes a titled section column with its
 * children inlined. Sections render side by side (a true mega panel) instead
 * of hover-nested flyouts.
 */
const getPanelGroups = (items: MegaMenuDropdownItem[]): PanelGroup[] => {
  const visible = getVisibleDropdownItems(items);
  const flat: PanelLeaf[] = [];
  const sections: PanelGroup[] = [];
  for (const item of visible) {
    if (item.flyout) {
      sections.push({
        label: item.label,
        items: item.flyout.map((leaf) => ({ ...leaf, flatIndex: -1 })),
      });
    } else {
      flat.push({ ...item, flatIndex: -1 });
    }
  }
  const groups = [...(flat.length ? [{ label: null, items: flat }] : []), ...sections];
  let i = 0;
  for (const group of groups) {
    for (const leaf of group.items) {
      leaf.flatIndex = i++;
    }
  }
  return groups;
};

/** True when a dropdown renders as a multi-column (wide) panel. */
const isWidePanel = (items: MegaMenuDropdownItem[]): boolean => {
  return getPanelGroups(items).length > 1;
};

/**
 * Merge `collapseInBar` items into one dropdown config for the synthesized
 * "More" bar item. Dropdown items contribute their sections (an item with an
 * index route keeps a plain link row so its landing page stays reachable);
 * simple route items become plain link rows in the untitled leading column.
 */
const buildMoreDropdown = (items: MegaMenuItem[]): MegaMenuDropdownItem[] => {
  const merged: MegaMenuDropdownItem[] = [];
  for (const item of items) {
    if (item.dropdown) {
      if (item.indexRoute) {
        merged.push({
          icon: item.icon,
          label: item.label,
          description: '',
          route: item.indexRoute,
        });
      }
      merged.push(...item.dropdown);
    } else if (item.route) {
      merged.push({ icon: item.icon, label: item.label, description: '', route: item.route });
    }
  }
  return merged;
};

/**
 * MegaMenu - Full mega-menu navigation component
 *
 * Features:
 * - Desktop: Horizontal nav with mega-menu dropdowns; `collapseInBar` items
 *   fold into a synthesized "More" dropdown so the bar fits its wrapper
 * - Mobile: Hamburger menu with slide-out panel and accordions
 * - Full keyboard navigation (arrow keys, Home/End, Escape)
 * - Dark mode support
 */
export class MegaMenu extends Component<MegaMenuSignature> {
  @service declare router: RouterService;

  @tracked openDropdownId: string | null = null;

  /** The bar trigger lights up when the current route lives inside its menu. */
  isItemActive = (item: MegaMenuItem): boolean => {
    const current = this.router.currentRouteName ?? '';
    const matches = (route?: string) => {
      if (!route) return false;
      const base = route.replace(/\.index$/, '');
      return current === route || current === base || current.startsWith(base + '.');
    };
    if (item.route) return matches(item.route);
    return (item.dropdown ?? []).some((d) =>
      d.flyout ? d.flyout.some((leaf) => matches(leaf.route)) : matches(d.route)
    );
  };

  @tracked isMobileMenuOpen = false;
  @tracked mobileExpandedId: string | null = null;
  @tracked mobileExpandedFlyout: string | null = null;
  @tracked focusedMenuIndex = -1;

  @cached
  get visibleNavItems() {
    return this.args.items.filter((item) => item.isVisible !== false);
  }

  /**
   * Items rendered on the desktop bar: `collapseInBar` items fold into a
   * single synthesized "More" dropdown (placed where the first collapsed
   * item sat) so the bar fits narrow desktop viewports and the capped
   * wrapper width. The mobile panel keeps the full item list.
   */
  @cached
  get barItems(): MegaMenuItem[] {
    const collapsed = this.visibleNavItems.filter((item) => item.collapseInBar);
    if (!collapsed.length) return this.visibleNavItems;
    const moreItem: MegaMenuItemWithDropdown = {
      id: 'tio-mega-menu-more',
      icon: PhDotsThree,
      label: this.args.moreLabel ?? 'More',
      // Inherit bar-position semantics from the first collapsed item so a
      // right-aligned item collapses into a right-aligned More trigger.
      alignEnd: collapsed[0]?.alignEnd,
      dropdown: buildMoreDropdown(collapsed),
    };
    const bar: MegaMenuItem[] = [];
    let morePlaced = false;
    for (const item of this.visibleNavItems) {
      if (!item.collapseInBar) {
        bar.push(item);
      } else if (!morePlaced) {
        bar.push(moreItem);
        morePlaced = true;
      }
    }
    return bar;
  }

  get brandLabel() {
    return this.args.brandLabel ?? 'TIO';
  }

  toggleDropdown = (id: string, event?: MouseEvent) => {
    if (this.openDropdownId === id) {
      this.openDropdownId = null;
    } else {
      this.openDropdownId = id;
    }
    // Safari doesn't focus <button> on click. Re-focus the trigger after
    // Glimmer re-renders so keyboard events (ArrowDown, Escape) fire on it.
    if (event) {
      const btn = event.currentTarget as HTMLElement;
      requestAnimationFrame(() => btn?.focus());
    }
  };

  closeDropdown = () => {
    this.openDropdownId = null;
  };

  // Deferred close for links - prevents interference with LinkTo navigation
  handleLinkClick = () => {
    setTimeout(() => {
      this.openDropdownId = null;
      this.isMobileMenuOpen = false;
      this.mobileExpandedId = null;
      this.mobileExpandedFlyout = null;
    }, 0);
  };

  handleActionClick = (item: MegaMenuItemWithAction) => {
    item.onClick();
    this.handleLinkClick();
  };

  toggleMobileMenu = () => {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (!this.isMobileMenuOpen) {
      this.mobileExpandedId = null;
      this.mobileExpandedFlyout = null;
    }
  };

  closeMobileMenu = () => {
    this.isMobileMenuOpen = false;
    this.mobileExpandedId = null;
    this.mobileExpandedFlyout = null;
  };

  toggleMobileExpanded = (id: string) => {
    if (this.mobileExpandedId === id) {
      this.mobileExpandedId = null;
    } else {
      this.mobileExpandedId = id;
    }
    this.mobileExpandedFlyout = null;
  };

  toggleMobileFlyout = (label: string) => {
    if (this.mobileExpandedFlyout === label) {
      this.mobileExpandedFlyout = null;
    } else {
      this.mobileExpandedFlyout = label;
    }
  };

  // Flattened leaf links of the open dropdown panel (sections inlined),
  // in DOM order — drives the roving-tab keyboard navigation.
  get currentDropdownItems() {
    const openItem = this.barItems.find((item) => item.id === this.openDropdownId);
    const items = openItem?.dropdown ?? [];
    return getPanelGroups(items).flatMap((group) => group.items);
  }

  // Focus a menu item by index using DOM query
  focusMenuItem = (index: number) => {
    const itemCount = this.currentDropdownItems.length;
    if (index >= 0 && index < itemCount) {
      this.focusedMenuIndex = index;
      // Query for the menu items in the DOM
      requestAnimationFrame(() => {
        const menuItems = document.querySelectorAll('.tio-mega-menu__panel [role="menuitem"]');
        const item = menuItems[index] as HTMLElement;
        item?.focus();
      });
    }
  };

  // Handle keyboard navigation on dropdown trigger
  handleTriggerKeyDown = (id: string, event: KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        this.closeDropdown();
        // Focus the trigger button
        {
          const trigger = (event.target as HTMLElement).closest('button');
          trigger?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggleDropdown(id);
        // Focus first menu item when opening
        if (this.openDropdownId === id) {
          this.focusedMenuIndex = -1;
          // Wait for render then focus first item
          requestAnimationFrame(() => {
            this.focusMenuItem(0);
          });
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.openDropdownId !== id) {
          this.toggleDropdown(id);
          requestAnimationFrame(() => {
            this.focusMenuItem(0);
          });
        } else {
          this.focusMenuItem(0);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.openDropdownId !== id) {
          this.toggleDropdown(id);
          requestAnimationFrame(() => {
            const lastIndex = this.currentDropdownItems.length - 1;
            this.focusMenuItem(lastIndex);
          });
        } else {
          const lastIndex = this.currentDropdownItems.length - 1;
          this.focusMenuItem(lastIndex);
        }
        break;
    }
  };

  // Handle keyboard navigation within the dropdown panel (flat roving tab
  // across all leaf links, sections included)
  handleMenuKeyDown = (index: number, event: KeyboardEvent) => {
    const itemCount = this.currentDropdownItems.length;

    switch (event.key) {
      case 'Escape': {
        event.preventDefault();
        this.closeDropdown();
        const trigger = document.querySelector('button[aria-expanded="true"]') as HTMLElement;
        trigger?.focus();
        break;
      }
      case 'ArrowDown':
        event.preventDefault();
        this.focusMenuItem((index + 1) % itemCount);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusMenuItem(index <= 0 ? itemCount - 1 : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusMenuItem(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusMenuItem(itemCount - 1);
        break;
      case 'Tab':
        // Allow tab to close menu and move to next element
        this.closeDropdown();
        break;
    }
  };

  <template>
    {{! Desktop Navigation }}
    <nav
      class="tio-mega-menu hidden md:block sticky top-0 z-40"
      role="navigation"
      aria-label="Main navigation"
      ...attributes
    >
      {{#if this.openDropdownId}}
        {{! Backdrop — sits under the bar row and panels; clicking it (i.e.
            clicking anywhere outside the open menu) closes the dropdown. }}
        <button
          type="button"
          class="fixed inset-0 cursor-default"
          aria-label="Close menu"
          tabindex="-1"
          {{on "click" this.closeDropdown}}
        ></button>
      {{/if}}
      <div class="relative {{if @isFullscreen 'px-6' 'max-w-7xl mx-auto px-6'}}">
        <div class="tio-mega-menu__bar tio-row-extra-tight">
          {{#if @logo}}
            {{#let @logo as |Logo|}}
              <Logo />
            {{/let}}
          {{else}}
            <span class="tio-mega-menu__brand tio-text-sm-semibold mr-3">{{this.brandLabel}}</span>
          {{/if}}
          {{#each this.barItems as |item|}}
            {{#if item.dropdown}}
              {{! Dropdown nav item — click the trigger to open/close. }}
              <div class="relative {{if item.alignEnd 'ml-auto'}}">
                <button
                  type="button"
                  class="tio-mega-menu__nav-item
                    {{if (eq this.openDropdownId item.id) 'is-open'}}
                    {{if (this.isItemActive item) 'is-current'}}"
                  aria-expanded="{{if (eq this.openDropdownId item.id) 'true' 'false'}}"
                  aria-haspopup="menu"
                  aria-label={{item.label}}
                  title={{item.label}}
                  {{on "click" (fn this.toggleDropdown item.id)}}
                  {{on "keydown" (fn this.handleTriggerKeyDown item.id)}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{18}} />
                  {{/let}}
                  <span>{{item.label}}</span>
                  <PhCaretDown
                    @size={{14}}
                    class="transition-transform
                      {{if (eq this.openDropdownId item.id) 'rotate-180'}}"
                  />
                </button>

                {{#if (eq this.openDropdownId item.id)}}
                  {{! Mega-menu dropdown — sections render side by side as columns
                      (a true mega panel); hover flyouts are gone. }}
                  <div
                    class="tio-mega-menu__panel absolute left-0 top-full mt-2 rounded-xl z-30 animate-in fade-in slide-in-from-top-2 duration-200
                      {{if (isWidePanel item.dropdown) 'tio-mega-menu__panel--wide' 'w-80'}}"
                    role="menu"
                    {{clampPanel}}
                  >
                    <div class="tio-mega-menu__columns p-2">
                      {{#each (getPanelGroups item.dropdown) as |group|}}
                        <div
                          class="tio-mega-menu__column"
                          role="group"
                          aria-label={{if group.label group.label item.label}}
                        >
                          {{#if group.label}}
                            <div
                              class="tio-text-overline tio-fg-muted px-3 pt-2 pb-1"
                            >{{group.label}}</div>
                          {{/if}}
                          {{#each group.items as |leaf|}}
                            <LinkTo
                              @route={{leaf.route}}
                              class="tio-mega-menu__item group"
                              role="menuitem"
                              tabindex={{if (eq this.focusedMenuIndex leaf.flatIndex) "0" "-1"}}
                              {{on "click" this.handleLinkClick}}
                              {{on "keydown" (fn this.handleMenuKeyDown leaf.flatIndex)}}
                            >
                              <div class="tio-mega-menu__icon">
                                {{#let leaf.icon as |LeafIcon|}}
                                  <LeafIcon @size={{20}} />
                                {{/let}}
                              </div>
                              <div class="tio-fill-min">
                                <div class="tio-row-tight">
                                  <span class="tio-mega-menu__title">{{leaf.label}}</span>
                                  {{#if leaf.stat}}
                                    <span
                                      class="tio-mega-menu__badge tio-text-xs-medium px-2 py-0.5 rounded-full"
                                    >{{leaf.stat}}</span>
                                  {{/if}}
                                </div>
                                <p class="tio-mega-menu__desc mt-0.5">{{leaf.description}}</p>
                              </div>
                            </LinkTo>
                          {{/each}}
                        </div>
                      {{/each}}
                    </div>

                    {{! CTA at bottom — only when the menu has a real index page }}
                    {{#if item.indexRoute}}
                      <div class="tio-mega-menu__cta p-3">
                        <LinkTo
                          @route={{item.indexRoute}}
                          class="tio-row-center gap-2 tio-mega-menu__cta-link w-full py-2 px-4 tio-text-sm-medium rounded-lg"
                          {{on "click" this.handleLinkClick}}
                        >
                          View
                          {{item.label}}
                          Dashboard →
                        </LinkTo>
                      </div>
                    {{/if}}
                  </div>
                {{/if}}
              </div>
            {{else if item.route}}
              {{! Simple nav item }}
              <LinkTo
                @route={{item.route}}
                class="tio-mega-menu__nav-item
                  {{if item.alignEnd 'ml-auto'}}
                  {{if (this.isItemActive item) 'is-current'}}"
                aria-label={{item.label}}
                title={{item.label}}
              >
                {{#let item.icon as |Icon|}}
                  <Icon @size={{18}} />
                {{/let}}
                {{#unless item.iconOnly}}
                  <span>{{item.label}}</span>
                {{/unless}}
              </LinkTo>
            {{else}}
              {{! Action nav item }}
              <button
                type="button"
                class="tio-mega-menu__nav-item {{if item.alignEnd 'ml-auto'}}"
                aria-label={{item.label}}
                title={{item.label}}
                {{on "click" (fn this.handleActionClick item)}}
              >
                {{#let item.icon as |Icon|}}
                  <Icon @size={{18}} />
                {{/let}}
                {{#unless item.iconOnly}}
                  <span>{{item.label}}</span>
                {{/unless}}
              </button>
            {{/if}}
          {{/each}}
          {{yield}}
        </div>
      </div>
    </nav>

    {{! Mobile Navigation }}
    <nav
      class="tio-mega-menu md:hidden sticky top-0 z-40"
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div class="tio-row-between px-4 py-3">
        <div class="tio-row-tight">
          {{#if @logo}}
            {{#let @logo as |Logo|}}
              <Logo />
            {{/let}}
          {{/if}}
          <span class="tio-fg-primary font-bold">{{this.brandLabel}}</span>
        </div>

        <div class="tio-row-tight">
          {{yield to="mobileActions"}}
          <button
            type="button"
            class="tio-mega-menu__icon-btn"
            aria-label="{{if this.isMobileMenuOpen 'Close menu' 'Open menu'}}"
            aria-expanded="{{if this.isMobileMenuOpen 'true' 'false'}}"
            {{on "click" this.toggleMobileMenu}}
          >
            {{#if this.isMobileMenuOpen}}
              <PhX @size={{24}} />
            {{else}}
              <PhList @size={{24}} />
            {{/if}}
          </button>
        </div>
      </div>

      {{#if this.isMobileMenuOpen}}
        {{! Mobile menu overlay }}
        <button
          type="button"
          class="fixed inset-0 bg-black/50 z-30"
          aria-label="Close menu"
          {{on "click" this.closeMobileMenu}}
        ></button>

        {{! Mobile menu panel }}
        <div
          class="tio-mega-menu__mobile-panel fixed top-0 right-0 bottom-0 w-80 max-w-[calc(100vw-48px)] z-40 shadow-xl overflow-y-auto animate-in slide-in-from-right duration-300"
        >
          <div class="tio-row-between tio-mega-menu__mobile-header px-4 py-3">
            <span class="tio-fg-primary font-bold">Menu</span>
            <button
              type="button"
              class="tio-mega-menu__icon-btn"
              aria-label="Close menu"
              {{on "click" this.closeMobileMenu}}
            >
              <PhX @size={{20}} />
            </button>
          </div>

          <div class="py-2">
            {{#each this.visibleNavItems as |item|}}
              {{#if item.dropdown}}
                {{! Accordion nav item }}
                <div class="tio-mega-menu__mobile-divider">
                  <button
                    type="button"
                    class="tio-row-between w-full px-4 py-3 text-left"
                    aria-expanded="{{if (eq this.mobileExpandedId item.id) 'true' 'false'}}"
                    {{on "click" (fn this.toggleMobileExpanded item.id)}}
                  >
                    <div class="tio-row">
                      {{#let item.icon as |Icon|}}
                        <Icon @size={{20}} class="tio-fg-muted" />
                      {{/let}}
                      <span class="tio-fg-primary font-medium">{{item.label}}</span>
                    </div>
                    <PhCaretDown
                      @size={{16}}
                      class="tio-mega-menu__caret transition-transform
                        {{if (eq this.mobileExpandedId item.id) 'rotate-180'}}"
                    />
                  </button>

                  {{#if (eq this.mobileExpandedId item.id)}}
                    <div class="tio-mega-menu__mobile-section pb-2">
                      {{#each (getVisibleDropdownItems item.dropdown) as |dropdownItem|}}
                        {{#if dropdownItem.flyout}}
                          {{! Nested accordion for flyout }}
                          <div>
                            <button
                              type="button"
                              class="tio-row-between tio-mega-menu__mobile-row w-full px-4 py-3 pl-12 text-left transition-colors"
                              aria-expanded="{{if
                                (eq this.mobileExpandedFlyout dropdownItem.label)
                                'true'
                                'false'
                              }}"
                              {{on "click" (fn this.toggleMobileFlyout dropdownItem.label)}}
                            >
                              <div class="tio-row">
                                {{#let dropdownItem.icon as |DropdownIcon|}}
                                  <DropdownIcon @size={{18}} class="tio-fg-disabled" />
                                {{/let}}
                                <span
                                  class="tio-fg-secondary tio-text-sm-medium"
                                >{{dropdownItem.label}}</span>
                              </div>
                              <PhCaretDown
                                @size={{14}}
                                class="tio-mega-menu__caret transition-transform
                                  {{if
                                    (eq this.mobileExpandedFlyout dropdownItem.label)
                                    'rotate-180'
                                  }}"
                              />
                            </button>

                            {{#if (eq this.mobileExpandedFlyout dropdownItem.label)}}
                              <div class="tio-mega-menu__mobile-subsection pb-1">
                                {{#each dropdownItem.flyout as |flyoutItem|}}
                                  <LinkTo
                                    @route={{flyoutItem.route}}
                                    class="tio-row tio-mega-menu__mobile-row--deep px-4 py-2.5 pl-20 transition-colors"
                                    {{on "click" this.handleLinkClick}}
                                  >
                                    {{#let flyoutItem.icon as |FlyoutIcon|}}
                                      <FlyoutIcon @size={{16}} class="tio-fg-disabled" />
                                    {{/let}}
                                    <span
                                      class="tio-mega-menu__mobile-leaf-label"
                                    >{{flyoutItem.label}}</span>
                                  </LinkTo>
                                {{/each}}
                              </div>
                            {{/if}}
                          </div>
                        {{else}}
                          {{! Regular dropdown link }}
                          <LinkTo
                            @route={{dropdownItem.route}}
                            class="tio-row tio-mega-menu__mobile-row px-4 py-3 pl-12 transition-colors"
                            {{on "click" this.handleLinkClick}}
                          >
                            {{#let dropdownItem.icon as |DropdownIcon|}}
                              <DropdownIcon @size={{18}} class="tio-fg-disabled" />
                            {{/let}}
                            <div class="flex-1">
                              <div class="tio-row-tight">
                                <span
                                  class="tio-fg-secondary tio-text-sm-medium"
                                >{{dropdownItem.label}}</span>
                                {{#if dropdownItem.stat}}
                                  <span
                                    class="tio-mega-menu__badge text-xs px-1.5 py-0.5 rounded"
                                  >{{dropdownItem.stat}}</span>
                                {{/if}}
                              </div>
                            </div>
                          </LinkTo>
                        {{/if}}
                      {{/each}}
                    </div>
                  {{/if}}
                </div>
              {{else if item.route}}
                {{! Simple mobile nav item }}
                <LinkTo
                  @route={{item.route}}
                  class="tio-row tio-mega-menu__mobile-divider tio-mega-menu__nav-item-mobile px-4 py-3 transition-colors"
                  {{on "click" this.handleLinkClick}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{20}} class="tio-fg-muted" />
                  {{/let}}
                  <span class="tio-fg-primary font-medium">{{item.label}}</span>
                </LinkTo>
              {{else}}
                {{! Simple mobile action item }}
                <button
                  type="button"
                  class="tio-row tio-mega-menu__mobile-divider tio-mega-menu__nav-item-mobile w-full px-4 py-3 transition-colors"
                  {{on "click" (fn this.handleActionClick item)}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{20}} class="tio-fg-muted" />
                  {{/let}}
                  <span class="tio-fg-primary font-medium">{{item.label}}</span>
                </button>
              {{/if}}
            {{/each}}
          </div>
        </div>
      {{/if}}
    </nav>
  </template>
}

export default MegaMenu;
